import { pick } from '@/utils/object'

import { gidToId, graphqlUnpack } from '@/utils/graphql'

import type { BundleSellingPlan, BundleProduct, BundleProductVariant } from '@/types/bundle'
import type { GraphqlConnection } from '@/types/graphql'
import type { GraphqlVarietyPack, GraphqlVarietyPackProduct } from '@/types/shopify/products'

interface GraphqlSellingPlanGroup {
  id: string
  sellingPlans: GraphqlConnection<BundleSellingPlan>
}

interface GraphqlBundleProduct {
  id: string
  title: string
  sellingPlanGroups?: GraphqlConnection<GraphqlSellingPlanGroup>
  variants: GraphqlConnection<BundleProductVariant>
}

export const bundleProductAttributes = (meta: any) => {
  const { id, variants } = meta?.reference || {}
  const variant = variants?.edges?.[0]?.node || {}
  const { id: variantId, sku } = variant
  return {
    key: `${gidToId(id)}/${gidToId(variantId)}`,
    sku
  }
}

export const imageRef = (meta: any) => meta?.reference?.image

export const unpackProduct = (rawProduct: any) => {
  const product: GraphqlVarietyPackProduct = {
    ...pick(rawProduct, 'id', 'handle', 'title', 'tags', 'description'),
    nid: gidToId(rawProduct.id),
    shortTitle: rawProduct.shortTitle?.value || ''
  }
  return product
}

export const unpackGraphqlVarietyPackProduct = (rawProducts: any) => {
  return graphqlUnpack<any>(rawProducts).map(rawProduct => {
    return unpackProduct(rawProduct)
  })
}

export const unpackVarietyPack = (rawProduct: any) => {
  // @ts-ignore
  const product: GraphqlVarietyPack = pick(rawProduct, 'id', 'handle', 'title', 'tags', 'description', 'descriptionHtml', 'createdAt', 'updatedAt', 'publishedAt', 'seo', 'featuredImage')
  product.nid = gidToId(product.id)
  product.ratingAverage = Number(rawProduct.ratingAverage?.value || 0)
  product.ratingCount = Number(rawProduct.ratingCount?.value || 0)
  product.flavorForwardImage = imageRef(rawProduct.flavorForwardImage)
  product.flavors = unpackGraphqlVarietyPackProduct(rawProduct.flavors?.references?.edges)
  product.skuMap = JSON.parse(rawProduct.skuMap?.value || 'null')
  product.quantityMap = JSON.parse(rawProduct.quantityMap?.value || '{}')
  product.variants = rawProduct.variants.edges.map(({ node })=> {
    const { id, sku } = node
    return {
      id: gidToId(id),
      sku
    }
  })
  return product
}

export const unpackVarietyPacks = (rawCollection: any) => {
  return graphqlUnpack<any>(rawCollection.products.edges).map(rawProduct => {
    return unpackVarietyPack(rawProduct)
  })
}

export const unpackBundleProduct = (product: GraphqlBundleProduct) => {
  const { id, title, sellingPlanGroups, variants } = product
  const unpacked: BundleProduct = {
    id,
    title,
    nid: gidToId(id),
    variants: variants.edges.map(i => {
      const { id, sku, title, price } = i.node
      const size = Number(title.match(/\d+/)?.[0])
      return {
        id,
        nid: gidToId(id),
        sku,
        title,
        size,
        price
      }
    })
  }
  if (sellingPlanGroups) {
    unpacked.sellingPlans = graphqlUnpack<GraphqlSellingPlanGroup>(sellingPlanGroups.edges)
      .map(sp => graphqlUnpack<BundleSellingPlan>(sp.sellingPlans.edges))
      .flat()
  }
  return unpacked
}

